// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { z } from 'zod';
import type { SambaInternalRole } from './samba-config.types';
import { SambaInternalRoleSchema } from './schemas/samba-config.schemas';

export type SambaImpersonationConfigRefreshCompletionV1 = {
    schemaVersion: 1;
    marker: 'native-impersonation-config-refresh-completed';
    requestId: string;
    triggerId: string;
    tabId: number;
    windowId: number;
    webContentsId: number;
    requestedTargetRoleAssignmentUuid: string;
    internalRole: SambaInternalRole | null;
};

export const SambaImpersonationConfigRefreshCompletionV1Schema: z.ZodType<SambaImpersonationConfigRefreshCompletionV1> =
    z.strictObject({
        schemaVersion: z.literal(1),
        marker: z.literal('native-impersonation-config-refresh-completed'),
        requestId: z.string().min(1),
        triggerId: z.string().min(1),
        tabId: z.number().int().positive(),
        windowId: z.number().int().positive(),
        webContentsId: z.number().int().positive(),
        requestedTargetRoleAssignmentUuid: z.string().uuid(),
        internalRole: SambaInternalRoleSchema.nullable(),
    });

export const SambaImpersonationConfigRefreshCompletionContract = {
    variants: {
        v1: {
            normalize: (input: unknown): SambaImpersonationConfigRefreshCompletionV1 =>
                SambaImpersonationConfigRefreshCompletionV1Schema.parse(input),
        },
    },
    writerVariant: 'v1',
} as const;

export type SambaImpersonationStopConfigRefreshCompletionV1 = {
    schemaVersion: 1;
    marker: 'native-impersonation-stop-config-refresh-completed';
    requestId: string;
    triggerId: string;
    tabId: number;
    windowId: number;
    webContentsId: number;
    internalRole: SambaInternalRole | null;
    portalContexts: {
        tabId: number;
        windowId: number;
        webContentsId: number;
        portalOrigin: string;
    }[];
};

export const SambaImpersonationStopConfigRefreshCompletionV1Schema: z.ZodType<SambaImpersonationStopConfigRefreshCompletionV1> =
    z.strictObject({
        schemaVersion: z.literal(1),
        marker: z.literal('native-impersonation-stop-config-refresh-completed'),
        requestId: z.string().min(1),
        triggerId: z.string().min(1),
        tabId: z.number().int().positive(),
        windowId: z.number().int().positive(),
        webContentsId: z.number().int().positive(),
        internalRole: SambaInternalRoleSchema.nullable(),
        portalContexts: z
            .array(
                z.strictObject({
                    tabId: z.number().int().positive(),
                    windowId: z.number().int().positive(),
                    webContentsId: z.number().int().positive(),
                    portalOrigin: z
                        .url()
                        .refine((value) => URL.canParse(value) && new URL(value).origin === value),
                })
            )
            .max(256)
            .refine(
                (portalContexts) =>
                    new Set(portalContexts.map((portalContext) => portalContext.tabId)).size ===
                        portalContexts.length &&
                    new Set(portalContexts.map((portalContext) => portalContext.webContentsId))
                        .size === portalContexts.length
            ),
    });

export const SambaImpersonationStopConfigRefreshCompletionContract = {
    variants: {
        v1: {
            normalize: (input: unknown): SambaImpersonationStopConfigRefreshCompletionV1 =>
                SambaImpersonationStopConfigRefreshCompletionV1Schema.parse(input),
        },
    },
    writerVariant: 'v1',
} as const;

export const AutomationCommandSocketTransitionSchema = z.enum(['OPEN', 'CLOSE', 'NONE']);

export type AutomationCommandSocketTransition = z.infer<
    typeof AutomationCommandSocketTransitionSchema
>;

export const SambaCommandSocketTransitionCompletionV1Schema = z.strictObject({
    schemaVersion: z.literal(1),
    marker: z.literal('native-command-socket-transition'),
    transition: AutomationCommandSocketTransitionSchema,
    socketOpen: z.boolean(),
});

export type SambaCommandSocketTransitionCompletionV1 = z.infer<
    typeof SambaCommandSocketTransitionCompletionV1Schema
>;

export const SambaCommandSocketTransitionCompletionContract = {
    variants: {
        v1: {
            normalize: (input: unknown): SambaCommandSocketTransitionCompletionV1 =>
                SambaCommandSocketTransitionCompletionV1Schema.parse(input),
        },
    },
    writerVariant: 'v1',
} as const;

export {
    DebugClipboardReadTextCommandArgsSchema,
    DebugClipboardReadTextCommandResultSchema,
    type DebugClipboardReadTextCommandArgs,
    type DebugClipboardReadTextCommandResult,
} from './samba-automation-clipboard.types';

export {
    WorkflowExecutionObservationV1Schema,
    WorkflowExecutionObservationLogV1Schema,
    WorkflowExecutionObservationContract,
    WorkflowExecutionObservationMessage,
    type WorkflowExecutionObservation,
    DebugRpaWorkflowExecutionIdentitySchema,
    DebugRpaRecentWorkflowExecutionSnapshotSchema,
    DebugRbsIpsRegisteredWorkflowSnapshotSchema,
    type SimpleRpaActiveWorkflowExecutionInfo,
    type SimpleRpaRecentWorkflowExecutionInfo,
    type DebugRbsIpsRegisteredWorkflowSnapshot,
} from './schemas/samba-rpa-execution-diagnostics.schemas';

export type {
    JsonArray,
    JsonObject,
    JsonPrimitive,
    JsonValue,
    SambaArch,
    SambaInstallerQaQuery,
    SambaLatestReleaseQuery,
    SambaLatestReleaseResponse,
    SambaLatestReleaseResponseV1,
    SambaPackageType,
    SambaPipeline,
    SambaPlatform,
    SambaTrack,
    SambaWindowsPackageRelease,
    SambaWindowsPackageReleaseV1,
    SambaWindowsTrackRelease,
    SambaWindowsTrackReleaseV1,
} from './common.types';
export type { WindowsArtifactQa } from './windows-artifact-qa.types';
export type { ReleaseIdentityV1 } from './native-release-catalog.types';
export { ReleaseIdentityV1Schema } from './schemas/native-release-catalog.schemas';
export { projectSambaLatestReleaseResponseV1Identity } from './schemas/common.schemas';
export type { BuildType, CompileTimeMetadata } from './compile-time/compile-time-metadata';
export type {
    AuthenticatedLogEntry,
    LogIngestRequest,
    LogMeta,
    LogWriteOptions,
    RequestLogEntry,
    LogService,
    LogSeverity,
    LogTopic,
} from './logging.types';
export type {
    GenericUxKeyModifier,
    RpaDomElementAction,
    GenericUxSequenceInit,
    GenericUxSequenceStep,
} from './samba-rpa-workflow.types';
export type {
    EffectiveConfigResponse,
    NavigateMenuIntegratedWebsite,
    NavigateMenuRpaBuilderWebsite,
    NavigateMenuUnmanagedAllowedWebsite,
    NavigateMenuWebsite,
    NavigateMenuWebsitesResponse,
    SambaAutoupdateConfig,
    SambaInternalRole,
} from './samba-config.types';
export type {
    CustomerDetails,
    GetReceiptDataResponse,
    ProcessAchPaymentRequest,
    ProcessAchPaymentResponse,
    ProcessCardTokenPaymentRequest,
    ProcessCardTokenPaymentResponse,
    ProcessCardNotPresentPaymentResponse,
    ProcessCloverCardNotPresentPaymentRequest,
    RecordTokenRequest,
    RecordTokenResponse,
    SambaGatewayTransactionDetailsFetchResult,
    TerminalAbortRequest,
    TerminalAbortResponse,
    TerminalSaleRequest,
    TerminalSaleResponse,
    GenericIpwTransactionDetails,
    TransactionDetails,
    TransactionDetailsCreateRequest,
} from './samba-payment.types';
export type { SubmitCardTokenPaymentRequest } from './payment.types';
export type {
    SidebarCardPaymentWorkflowCommonFields,
    SidebarManualCardPaymentTokenizationFields,
    SidebarManualCardPaymentWorkflowInit,
    SidebarManualCardPaymentWorkflowPaymentFields,
    SidebarPaymentWorkflowFeeFields,
    SidebarSavedCardPaymentWorkflowInit,
} from './sidebar-payment-workflow.types';
export type {
    MerchantCustomerRecordDetailsResponse,
    MerchantCustomerRecordIpwLookupResponse,
    UpsertIpwMerchantCustomerRecordRequest,
    UpsertIpwMerchantCustomerRecordResponse,
} from './customer.types';
export type {
    GenerateReceiptPdfRequest,
    GenerateReceiptPdfResponse,
    ReceiptGenerationErrorCode,
} from './receipt-ipc.types';
export type {
    DealertrackArStatementCustomersTransactionDetails,
    DealertrackDealTransactionDetails,
    DealertrackLeasingTransactionDetails,
    DealertrackLeasingTransactionExtension,
    DealertrackPartsTransactionDetails,
    DealertrackPartsTransactionExtension,
    DealertrackServiceTransactionDetails,
    DealertrackServiceTransactionExtension,
    DealertrackTransactionDetails,
} from './types/ipw/dealertrack/dealertrack-transaction-details';
export {
    DealertrackScreenId,
    DealertrackScreenIdSchema,
    DealertrackScreenIdValues,
} from './types/ipw/dealertrack/dealertrack-screen-id';
export {
    AppStatePatchSchema,
    AppStateSchema,
    CardPaymentScreenMode,
    createGenericTransactionDetailsDraft,
    IpsWorkspaceStateV1Schema,
    PaymentsHomeStatePatchSchema,
    ReconciliationTargetKind,
    ReconciliationTargetSchema,
    StateType,
    TerminalPaymentScreenMode,
} from './sidebar-app-state.types';
export {
    buildRbsIpsStableTextReadResult,
    parseRbsIpsDisplayedAmount,
    rbsIpsReadValueMatchesStableText,
    removeRbsIpsStableTextFromReadValue,
    type RbsIpsDisplayedAmountParseResult,
    type RbsIpsStableTextReadResult,
} from './rbs-ips-read-preview.types';
export type {
    AppState,
    AppStatePatch,
    IpsPaymentsHomeAppState,
    IpsWorkspaceStateV1,
    PaymentsHomeStatePatch,
    ReconciliationTarget,
} from './sidebar-app-state.types';
export type { SupportedIpgSystemId } from './types/third-party-system.enums';
export type * from './PaymentModels';
export type { SafeParseResult } from './zod-utils';
export {
    ApiError,
    type ApiValidationError,
    type FetchImplementation,
    type FetchRequestInit,
    type HttpRequestHeaders,
    type PillarApiClientOptions,
    type PillarApiRequestOptions,
} from './pillar-api-core';
export {
    GenericUxDelayMsSchema,
    GenericUxFramePathSchema,
    GenericUxKeyModifierSchema,
    GenericUxSequenceInitSchema,
    GenericUxSequenceStepSchema,
    GenericUxTimeoutMsSchema,
} from './schemas/samba-rpa-workflow.schemas';
export {
    GENERIC_IPW_INVOICE_ID_MAX_LENGTH,
    GenericIpwTransactionDetailsSchema,
    GenericIpwTransactionDetailsV1Schema,
} from './schemas/payment.schemas';
export { NumericIpgSystemIdSchema } from './schemas/third-party-system.schemas';
export {
    ConfigResponseSchema,
    EffectiveConfigResponseSchema,
    NavigateMenuIpgWebsiteSchema,
    NavigateMenuIpwWebsiteSchema,
    NavigateMenuPortalWebsiteSchema,
    NavigateMenuRpaBuilderWebsiteSchema,
    NavigateMenuTargetKindSchema,
    NavigateMenuUnmanagedAllowedWebsiteSchema,
    NavigateMenuWebsiteSchema,
    NavigateMenuWebsitesResponseSchema,
    SambaAutoupdateConfigSchema,
    SambaInternalRoleSchema,
} from './schemas/samba-config.schemas';
export {
    InstallerDownloadLinkTarget,
    NavigateMenuTargetKind,
    NavigateMenuWebsiteLinkTarget,
} from './samba-config.types';
export { createPillarApiWithOptions, type SambaPillarApiClient } from './samba-api-client';
export { runInBackground } from './async-utils';
export {
    AnchorbaseActionLinkContract,
    AnchorbaseActionLinkV1Schema,
    parseAnchorbaseActionLinkV1,
    writeAnchorbaseActionLinkV1,
    type AnchorbaseActionLink,
    type AnchorbaseLoginActionLink,
    type AnchorbaseNavigateActionLink,
    type AnchorbaseRpaActionLink,
} from './schemas/anchorbase-action-link.schemas';
export {
    defaultIntegratedWebsiteAllowedWebsiteBySystemId,
    defaultIntegratedWebsiteAllowedUrlPatternsBySystemId,
    defaultIntegratedWebsiteDisplayNameBySystemId,
    defaultIntegratedWebsiteLaunchUrlBySystemId,
    defaultIntegratedWebsiteSystemIds,
    defaultIpxCredentialLaunchUrlBySystemId,
    ipwConfigDealertrack,
} from './backend_config.constants';
export {
    IpWebsiteType,
    AddressSchema,
    AddressCanadaSchema,
    AddressUSSchema,
    DealertrackSelectedReconciliationConfigurationV1Schema,
    DealertrackSelectedReconciliationConfigurationContract,
    DealertrackSelectedReconciliationConfigurationParser,
    writeDealertrackSelectedReconciliationConfigurationV1,
    type DealertrackSelectedReconciliationConfigurationV1,
    type IpwReconciliationConfig,
    IpwConfigDealertrackSchema,
    UrlPatternsRuleAction,
    MerchantConfigSchema,
    resolveMerchantConfigEnablePaymentsInPillar,
    RpaBuilderWebsiteConfigSchema,
    PlatformPaymentMethodsSchema,
    RpaModuleName,
    SessionPersistenceType,
    AllowedWebsiteSchemaUnmanaged,
    type Address,
    type AddressUS,
    type AllowedWebsite,
    type UnmanagedAllowedWebsite,
    type UrlPatternsRules,
    type UrlPatternsRule,
    type MerchantConfig,
    type RpaBuilderWebsiteConfig,
    type SambaCredentialMetadata,
    type PlatformPaymentMethods,
    allowedWebsitesOfMerchantConfig,
    isRpaModuleName,
    rpaModulesOfMerchantConfig,
} from './backend_config.types';
export {
    AllowedWebsiteTemplateSystemIdSchema,
    CreateAllowedWebsiteTemplateRequestSchema,
    type AllowedWebsiteTemplateSystemId,
    type CreateAllowedWebsiteTemplateRequest,
} from './allowed-website-template.types';
export { COMPLIANCE_LOG_TOPIC } from './logging.types';
export { PaymentResultSchema, TerminalAbortResultSchema } from './PaymentModels';
export { CardType } from './samba-payment.types';
export type { PaymentResult, TerminalAbortResult } from './PaymentModels';
export {
    JsonValueSchema,
    JsonObjectSchema,
    JsonValuePrunedSchema,
    PhoneNumberSchema,
    pruneString,
    SambaLatestReleaseResponseSchema,
    SambaLatestReleaseResponseV1Schema,
    SambaInstallerQaQuerySchema,
} from './schemas/common.schemas';
export { WindowsArtifactQaSchema } from './schemas/windows-artifact-qa.schemas';
export {
    DealertrackArStatementCustomersTransactionDetailsV1Schema,
    DealertrackDealTransactionDetailsV1Schema,
    DealertrackLeasingTransactionDetailsV1Schema,
    DealertrackPartsTransactionDetailsV1Schema,
    DealertrackServiceTransactionDetailsV1Schema,
    DealertrackTransactionDetailsSchema,
} from './schemas/ipw/dealertrack/dealertrack-transaction-details.schemas';
export {
    AuthenticatedLogEntrySchema,
    LogIngestRequestSchema,
    RequestLogEntrySchema,
    LogServiceSchema,
    LogSeveritySchema,
} from './schemas/logging.schemas';
export { MaskedPanSchema } from './schemas/masked_pan.schemas';
export {
    MerchantCustomerRecordDetailsResponseSchema,
    MerchantCustomerRecordIpwLookupResponseSchema,
    UpsertIpwMerchantCustomerRecordRequestSchema,
    UpsertIpwMerchantCustomerRecordResponseSchema,
} from './schemas/customer.schemas';
export {
    CustomerDetailsSchema,
    GetReceiptDataResponseSchema,
    ProcessAchPaymentRequestSchema,
    ProcessAchPaymentResponseSchema,
    ProcessCardTokenPaymentRequestSchema,
    ProcessCardTokenPaymentResponseSchema,
    RecordTokenRequestSchema,
    RecordTokenResponseSchema,
    SambaGatewayTransactionDetailsFetchResultSchema,
    TerminalAbortRequestSchema,
    TerminalAbortResponseSchema,
    TerminalSaleRequestSchema,
    TerminalSaleResponseSchema,
    TransactionDetailsSchema,
} from './schemas/samba-payment.schemas';
export {
    ProcessCardNotPresentPaymentResponseSchema,
    SubmitCardTokenPaymentRequestSchema,
} from './schemas/payment.schemas';
export { ProcessCloverCardNotPresentPaymentRequestSchema } from './schemas/ipg/clover/clover-card-not-present-payment.schemas';
export {
    SidebarCardPaymentWorkflowCommonFieldsSchema,
    SidebarManualCardPaymentTokenizationFieldsSchema,
    SidebarManualCardPaymentWorkflowInitSchema,
    SidebarManualCardPaymentWorkflowPaymentFieldsSchema,
    SidebarPaymentWorkflowFeeFieldsSchema,
    SidebarPaymentWorkflowNonEmptyStringSchema,
    SidebarPaymentWorkflowPanPrefixSchema,
    SidebarPaymentWorkflowUuidSchema,
    SidebarSavedCardPaymentWorkflowInitSchema,
} from './sidebar-payment-workflow.schemas';
export {
    GenerateReceiptPdfRequestSchema,
    GenerateReceiptPdfResponseSchema,
} from './schemas/receipt-ipc.schemas';
export type { ActiveRoleAssignmentResponse } from './auth.types';
export { ActiveRoleAssignmentResponseSchema } from './schemas/auth.schemas';
export type {
    CredentialRotationBeginRequest,
    CredentialRotationBeginResolvedResponse,
    CredentialRotationBeginResponse,
    CredentialRotationDomFailureEvidence,
    CredentialRotationDomSuccessEvidence,
    CredentialRotationFailRequest,
    CredentialRotationInjectedRequest,
    CredentialRotationInjectionEvidence,
    CredentialRotationPromoteRequest,
    CredentialRotationPromoteResponse,
    CredentialRotationSubmittedRequest,
    CredentialRotationSubmissionTriggerEvidence,
    IpxCredentialFieldName,
    IpxCredentialRuntimeBootstrapRequest,
    IpxCredentialRuntimeBootstrapResponse,
    IpxCredentialRuntimeMatchedUrlData,
    IpxCredentialRuntimeResolvedResponse,
    IpxCredentialRuntimeResolveRequest,
    IpxCredentialRuntimeResolveResponse,
    IpxCredentialRuntimeRoleScope,
    IpxCredentialRuntimeWebResourceModelProjection,
    IpxCredentialRuntimeWorkflowProjection,
    ManualCredentialCaptureRequest,
    ManualCredentialCaptureResponse,
    RpaBuilderActiveIpxContext,
    RpaBuilderBootstrapRequest,
    SupportedCredentialSystemId,
} from './secret.types';
export {
    CredentialRotationBeginResult,
    CredentialRotationEvidenceKind,
    CredentialRotationPromoteResult,
    CredentialType,
    IpxCredentialRuntimeResolveResult,
    ManualCredentialCaptureResult,
    SupportedCredentialSystemIds,
    listIpxCredentialFieldConfigPublicFields,
    listIpxCredentialFieldConfigSecretFields,
} from './secret.types';
export {
    IpxCredentialRuntimeBootstrapResponseSchema,
    IpxCredentialRuntimeRoleScopeSchema,
    IpxCredentialRuntimeWebResourceModelProjectionSchema,
    IpxCredentialRuntimeWorkflowProjectionSchema,
    RpaBuilderActiveIpxContextSchema,
    RpaBuilderBootstrapRequestSchema,
    SupportedCredentialSystemIdSchema,
} from './schemas/secret.schemas';
export { ThirdPartySystemIdSchema } from './schemas/third-party-system.schemas';
export type {
    IpxCredentialFlowFieldBinding,
    IpxFlowOutcomePredicate,
    IpxFlowOutcomePredicateNormalizer,
    IpxFlowUrlPrerequisite,
    IpxDomRpaWorkflow,
    IpxDomRpaWorkflowStep,
    IpxDomRpaWorkflowStepForKind,
    IpxManualCaptureTriggerPolicy,
    IpxWebResourceDomElementDescriptor,
    IpxWebResourceElementTextAnchor,
    IpxWebResourceDomElementMap,
    IpxWebResourceElementEvidence,
    IpxWebResourceElementKindKey,
    IpxWebResourceElementKindSupportedAction,
    IpxWebResourceElementKindSupportedActionByKind,
    IpxWebResourceModelDocument,
    IpxWebResourceModelValidationIssue,
    IpxWebResourceModelValidationResult,
    RpaDomAttributeScreenDiscriminantExpression,
    RpaCredentialFieldSelector,
    RpaCredentialFieldSelectorMap,
    RpaDomSelector,
    RpaDomSelectorFieldMap,
    RpaDomTextScreenDiscriminantExpression,
    RpaSanitizedDomSnapshot,
    RpaSanitizedDomStableAttributeMap,
    RpaScreenDefinition,
    RpaScreenDetectionPredicate,
    RpaScreenDetectionProfile,
    RpaScreenDiscriminantExpression,
    RpaScreenDiscriminantProfile,
    RpaScreenKey,
} from './samba-rpa-workflow.types';
export type {
    IpxRbsWorkflowCompositeRuntimeProjection,
    IpxRbsWorkflowElementBinding,
    IpxRbsWorkflowInstanceRuntimeProjection,
    IpxRbsWorkflowRuntimeBootstrapRequest,
    IpxRbsWorkflowRuntimeBootstrapResponse,
    IpxRbsWorkflowRuntimeOpenerTarget,
    IpxRbsWorkflowRuntimeProjection,
    IpxRbsWorkflowWebsiteScope,
} from './rbs-ips-workflow.types';
export {
    IpxRbsWorkflowBindingValueKind,
    IpxRbsWorkflowClass,
    IpxRbsWorkflowConstantBindingKey,
    IpxRbsWorkflowStepBindingSupportedVersions,
    rbsBindingAppliesToStep,
    IpxRbsWorkflowEditorPolicies,
    IpxRbsWorkflowWebsiteScopeKind,
    formatRbsWorkflowBindingLabel,
} from './rbs-ips-workflow.types';
export {
    RBS_IPS_ACTION_FAILED_ERROR_CODE,
    RbsIpsActionFailureResultSchema,
    describeRbsIpsActionFailure,
    formatRbsIpsActionFailureError,
    readRbsIpsActionFailureError,
} from './rbs-ips-action-failure';
export type { RbsIpsActionFailureResult } from './rbs-ips-action-failure';
export {
    IpxFlowFieldBindingSource,
    IpxManualCaptureTriggerKind,
    IpxWebResourceElementKind,
    IpxWebResourceElementKindDefaultAction,
    IpxWebResourceElementKindDefaultActionByKind,
    IpxWebResourceElementKindSupportedActions,
    IpxWebResourceElementKindSupportedActionsByKind,
    IpxWebResourceModelValidationIssueCode,
    getIpxWebResourceDomElement,
    listIpxWebResourceDomElements,
    validateIpxDomRpaWorkflow,
    validateIpxWebResourceModel,
} from './samba-rpa-workflow.types';
export {
    IpxCredentialFlowFieldBindingSchema,
    IpxCredentialFlowFieldNameSchema,
    IpxDomRpaWorkflowStorageVersionSchema,
    IpxDomRpaWorkflowV1Schema,
    IpxDomRpaWorkflowV2Schema,
    RpaDomElementActionSchema,
    IpxFlowElementAbsentOutcomePredicateSchema,
    IpxFlowElementPresentOutcomePredicateSchema,
    IpxFlowFieldBindingSourceSchema,
    IpxFlowMappedTextContainsOutcomePredicateSchema,
    IpxFlowMappedTextEqualsOutcomePredicateSchema,
    IpxFlowOutcomePredicateNormalizerSchema,
    IpxFlowOutcomePredicateSchema,
    IpxFlowScreenReachedOutcomePredicateSchema,
    IpxFlowUrlPatternPrerequisiteSchema,
    IpxFlowUrlPrerequisiteSchema,
    IpxButtonDomRpaWorkflowStepSchema,
    IpxDomRpaWorkflowStepListSchema,
    IpxDomRpaWorkflowStepSchema,
    IpxFieldDomRpaWorkflowStepSchema,
    IpxManualCaptureTriggerKindSchema,
    IpxManualCaptureTriggerPolicySchema,
    IpxScreenMarkerDomRpaWorkflowStepSchema,
    IpxSecretDomRpaWorkflowStepSchema,
    IpxTextDomRpaWorkflowStepSchema,
    IpxWebResourceElementKindSchema,
    IpxWebResourceElementEvidenceSchema,
    IpxWebResourceElementTextAnchorSchema,
    IpxWebResourceModelDocumentSchema,
    IpxWebResourceModelDocumentV1Parser,
    IpxWebResourceModelDocumentV1Schema,
    IpxWebResourceModelDocumentV2Parser,
    IpxWebResourceModelDocumentV2Schema,
    IpxWebResourceModelDocumentStorageVersionSchema,
    StoredIpxDomRpaWorkflowParser,
    StoredIpxDomRpaWorkflowSchema,
    StoredIpxWebResourceModelDocumentParser,
    StoredIpxWebResourceModelDocumentSchema,
    serializeIpxDomRpaWorkflowV3,
    serializeIpxWebResourceModelDocumentV4,
    RpaCredentialFieldSelectorMapSchema,
    RpaCredentialFieldSelectorSchema,
    RpaDomAttributeScreenDiscriminantExpressionSchema,
    RpaDomSelectorFieldMapSchema,
    RpaDomSelectorSchema,
    RpaDomTextScreenDiscriminantExpressionSchema,
    RpaSanitizedDomSnapshotSchema,
    RpaSanitizedDomStableAttributeMapSchema,
    RpaScreenDefinitionSchema,
    RpaScreenDetectionPredicateSchema,
    RpaScreenDetectionProfileSchema,
    RpaScreenDiscriminantExpressionSchema,
    RpaScreenDiscriminantProfileSchema,
    RpaScreenKeySchema,
} from './schemas/samba-rpa-workflow.schemas';
export {
    IpxRbsWorkflowElementBindingListStorageVersionSchema,
    IpxRbsWorkflowElementBindingListSchema,
    IpxRbsWorkflowElementBindingListV1Schema,
    IpxRbsWorkflowElementBindingListV2Schema,
    IpxRbsWorkflowElementBindingListV3Schema,
    IpxRbsWorkflowClassSchema,
    IpxRbsWorkflowRuntimeBootstrapRequestSchema,
    IpxRbsWorkflowRuntimeBootstrapResponseSchema,
    IpxRbsWorkflowRuntimeProjectionSchema,
    StoredIpxRbsWorkflowElementBindingListParser,
    StoredIpxRbsWorkflowElementBindingListSchema,
    serializeIpxRbsWorkflowElementBindingListV4,
} from './schemas/rbs-ips-workflow.schemas';
export {
    applyRbsValueTransform,
    describeRbsValueTransformFailure,
    readRbsValueTransformFailedText,
    formatRbsIpsValueTransformFailureError,
    RBS_IPS_VALUE_TRANSFORM_FAILED_ERROR_CODE,
    RbsIpsValueTransformFailureResultSchema,
    readRbsIpsValueTransformFailureError,
    describeRbsValueTransformStep,
    IpxRbsValueTransformOp,
    IpxRbsValueTransformOccurrence,
    IpxRbsValueTransformSupportedVersions,
    IpxRbsValueTransformV1Schema,
} from './rbs-value-transform';
export type {
    IpxRbsTextMatch,
    IpxRbsValueTransformResult,
    IpxRbsValueTransformStep,
    IpxRbsValueTransformV1,
    RbsIpsValueTransformFailureResult,
} from './rbs-value-transform';
export { IpxRbsWorkflowWebsiteScopeSchema } from './schemas/rbs-website-scope.schemas';
export {
    formatThirdPartySystemName,
    IpgSystemId,
    IpwSystemId,
    IpwSystemIdKey,
    isThirdPartySystemId,
    ThirdPartySystemId,
} from './types/third-party-system.enums';
export {
    GenericThirdPartySystemId,
    TransactionDetailsTypeId,
} from './types/transaction-details.enums';
export { isEmpty, isNotEmpty } from './utils';
